import { useMemo } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import type { ISpawnableObject } from '@interfaces/event';

export interface UseSpawnableObjectsResult {
  /** Types the streamed build says the web may place, in schema order. */
  types: ISpawnableObject[];
  // False when the build published no `spawnableObjects` key at all — an older
  // build without the exporter, or the capture checkbox off. Absence means
  // UNKNOWN, not "no spawnable types", so callers render no spawn UI rather
  // than an empty picker.
  available: boolean;
}

/**
 * Reads the runtime-spawnable type schema off the streamed build's own record
 * (`clientParams.spawnableObjects`) — the same read-from-the-build pattern as
 * useMapEditorCatalog, and like it deliberately never merged with any other
 * source: whatever this build published is what the picker offers.
 *
 * @returns The schema's types plus whether the build published a schema at all.
 */
export const useSpawnableObjects = (): UseSpawnableObjectsResult => {
  const { pixelStreamingClient } = useMytaverseEvent();

  const spawnableObjects = pixelStreamingClient?.clientParams?.spawnableObjects;

  return useMemo(() => {
    if (!Array.isArray(spawnableObjects)) {
      return { types: [], available: false };
    }

    return {
      types: spawnableObjects.filter(
        (spawnableObject) =>
          !!spawnableObject?.typeId && !!spawnableObject?.displayName,
      ),
      available: true,
    };
  }, [spawnableObjects]);
};
